import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, CircleAlert, ExternalLink, LoaderCircle } from 'lucide-react'
import type { ComponentData } from '@/lib/commandsApi'
import type { Candidate, PickKind } from '@/lib/commands'
import { actionRows, buttonTone, safeLinkUrl, selectDefaults, selectKind, selectLimits, selectionAllowed } from '@/lib/interactions'
import { emojiImageUrl } from '@/lib/markdown'
import { revealIn } from '@/lib/reveal'
import { cx } from '@/lib/cx'
import { ComponentType, type ActionRow, type ButtonComponent, type ComponentEmoji, type Message, type SelectComponent } from '@/types/api'
import { CandidateIcon } from './CandidateIcon'
import styles from './MessageComponents.module.css'

/** Что кнопкам и спискам нужно от чата: как выполнить нажатие и из кого выбирать. */
export interface ComponentEnv {
  /** null — получилось; строка — что показать под строкой с кнопкой. */
  run: (message: Message, data: ComponentData) => Promise<string | null>
  /** Участники, роли и каналы чата для списков 5–8. */
  candidates: (kind: PickKind, query: string, channelTypes?: readonly number[]) => Candidate[]
}

/** Сколько висит «Взаимодействие не удалось» под строкой. */
const FAILURE_MS = 5000

/** Строки кнопок и списков под сообщением бота. Без env (не чат) — только посмотреть. */
export function MessageComponents({ message, env }: { message: Message; env?: ComponentEnv | undefined }) {
  const rows = useMemo(() => actionRows(message.components), [message.components])
  if (!rows.length) return null
  return (
    <div className={styles.rows}>
      {rows.map((row, i) => (
        // бот меняет кнопки правкой сообщения: новая строка — новое состояние, а не чужая загрузка
        <Row key={`${i}:${row.components.map((c) => ('custom_id' in c && c.custom_id) || ('url' in c && c.url) || '').join('|')}`} message={message} row={row} env={env} />
      ))}
    </div>
  )
}

const isSelect = (c: ActionRow['components'][number]): c is SelectComponent => c.type !== ComponentType.Button

function Row({ message, row, env }: { message: Message; row: ActionRow; env: ComponentEnv | undefined }) {
  const [failure, setFailure] = useState<string | null>(null)
  const timer = useRef(0)
  useEffect(() => () => window.clearTimeout(timer.current), [])

  const run = useCallback(
    async (data: ComponentData) => {
      if (!env) return
      window.clearTimeout(timer.current)
      setFailure(null)
      const error = await env.run(message, data)
      if (!error) return
      setFailure(error)
      timer.current = window.setTimeout(() => setFailure(null), FAILURE_MS)
    },
    [env, message],
  )

  // в строке либо до пяти кнопок, либо один список
  const select = row.components.find(isSelect)
  return (
    <div className={styles.rowWrap}>
      {select ? (
        <Select select={select} run={run} candidates={env?.candidates} />
      ) : (
        <div className={styles.row}>
          {row.components.map((c, i) => (c.type === ComponentType.Button ? <Button key={c.custom_id ?? c.url ?? i} button={c} run={run} enabled={Boolean(env)} /> : null))}
        </div>
      )}
      {failure ? (
        <p className={styles.failure} role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {failure}
        </p>
      ) : null}
    </div>
  )
}

function EmojiView({ emoji }: { emoji: ComponentEmoji }) {
  if (emoji.id) return <img className={styles.emoji} src={emojiImageUrl(emoji.id, emoji.animated)} alt={emoji.name ? `:${emoji.name}:` : ''} draggable={false} />
  return emoji.name ? <span className={styles.emojiChar} aria-hidden="true">{emoji.name}</span> : null
}

function Button({ button, run, enabled }: { button: ButtonComponent; run: (data: ComponentData) => Promise<void>; enabled: boolean }) {
  const [busy, setBusy] = useState(false)
  const tone = buttonTone(button.style)
  const content = (
    <>
      {button.emoji ? <EmojiView emoji={button.emoji} /> : null}
      {button.label ? <span className={styles.label}>{button.label}</span> : null}
    </>
  )

  if (tone === 'link') {
    // адрес проверил сервер, но в href уходит только http(s): javascript: из чужого бота не выполнится
    const href = safeLinkUrl(button.url)
    if (!href || button.disabled) {
      return (
        <span className={cx(styles.button, styles.link, styles.disabled)} role="link" aria-disabled="true">
          {content}
          <ExternalLink size={14} aria-hidden="true" />
        </span>
      )
    }
    return (
      <a className={cx(styles.button, styles.link)} href={href} target="_blank" rel="noopener noreferrer" data-tooltip={href}>
        {content}
        <ExternalLink size={14} aria-hidden="true" />
      </a>
    )
  }

  const click = async () => {
    if (busy || !button.custom_id) return
    setBusy(true)
    try {
      await run({ component_type: ComponentType.Button, custom_id: button.custom_id })
    } finally {
      setBusy(false)
    }
  }
  return (
    <button type="button" className={cx(styles.button, styles[tone])} disabled={button.disabled || !enabled} aria-busy={busy || undefined} onClick={() => void click()}>
      {/* подпись остаётся в раскладке, а крутилка встаёт поверх: кнопка не меняет ширину */}
      <span className={cx(styles.content, busy && styles.contentBusy)}>{content}</span>
      {busy ? (
        <span className={styles.spinner} aria-hidden="true">
          <LoaderCircle size={18} />
        </span>
      ) : null}
    </button>
  )
}

/** Строка выпадающего списка: вариант бота или участник, роль, канал. */
interface Item {
  key: string
  value: string
  label: string
  description?: string | null | undefined
  emoji?: ComponentEmoji | null | undefined
  candidate?: Candidate | undefined
}

const PLACEHOLDER: Record<PickKind, string> = {
  user: 'Выберите участника',
  role: 'Выберите роль',
  mentionable: 'Выберите участника или роль',
  channel: 'Выберите канал',
}

function Select({ select, run, candidates }: { select: SelectComponent; run: (data: ComponentData) => Promise<void>; candidates: ComponentEnv['candidates'] | undefined }) {
  const kind = selectKind(select.type)
  const { max } = selectLimits(select)
  const multi = max > 1
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [busy, setBusy] = useState(false)
  // выбор, пока список открыт или ответ ещё не пришёл; null — то, что задал бот (default)
  const [picked, setPicked] = useState<Item[] | null>(null)
  // кнопка списка — якорь выпадающего меню; в состоянии, а не в ref: меню рисуется от неё
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null)
  const listId = useId()

  const own = useMemo<Item[]>(() => (select.options ?? []).map((o) => ({ key: o.value, value: o.value, label: o.label, description: o.description, emoji: o.emoji })), [select.options])
  const initial = useMemo(() => {
    const defaults = selectDefaults(select)
    return own.filter((i) => defaults.includes(i.value))
  }, [own, select])
  const items = useMemo<Item[]>(
    () =>
      kind
        ? (candidates?.(kind, query, select.channel_types) ?? []).map((c) => ({ key: c.key, value: String(c.value), label: c.label, description: c.kind === 'user' ? c.hint : undefined, candidate: c }))
        : own,
    [kind, candidates, query, select.channel_types, own],
  )
  const current = picked ?? initial
  const enabled = Boolean(candidates) && !select.disabled

  const submit = async (values: Item[]) => {
    if (!selectionAllowed(select, values.map((v) => v.value))) {
      setPicked(null)
      return
    }
    setBusy(true)
    try {
      await run({ component_type: select.type, custom_id: select.custom_id, values: values.map((v) => v.value) })
    } finally {
      // после ответа список снова показывает то, что задал бот: новое состояние он пришлёт правкой
      setBusy(false)
      setPicked(null)
    }
  }

  const choose = (item: Item) => {
    if (!multi) {
      setOpen(false)
      setQuery('')
      setPicked([item])
      void submit([item])
      return
    }
    setPicked((cur) => {
      const base = cur ?? initial
      if (base.some((i) => i.value === item.value)) return base.filter((i) => i.value !== item.value)
      return base.length >= max ? base : [...base, item]
    })
  }

  // закрыли список с несколькими вариантами — выбор уходит боту, если он поменялся (как у Discord)
  const close = (returnFocus: boolean) => {
    setOpen(false)
    setQuery('')
    if (multi && picked) {
      const same = picked.length === initial.length && picked.every((i) => initial.some((d) => d.value === i.value))
      if (same) setPicked(null)
      else void submit(picked)
    }
    if (returnFocus) trigger?.focus()
  }

  const placeholder = select.placeholder || (kind ? PLACEHOLDER[kind] : 'Выберите вариант')
  return (
    <>
      <button
        ref={setTrigger}
        type="button"
        className={cx(styles.select, busy && styles.selectBusy)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-busy={busy || undefined}
        disabled={!enabled}
        onClick={() => {
          if (busy) return
          if (open) close(false)
          else setOpen(true)
        }}
      >
        {current.length ? (
          <span className={styles.selectValue}>
            {current.length === 1 && current[0]?.emoji ? <EmojiView emoji={current[0].emoji} /> : null}
            <span className={styles.selectText}>{current.map((i) => i.label).join(', ')}</span>
          </span>
        ) : (
          <span className={cx(styles.selectValue, styles.placeholder)}>
            <span className={styles.selectText}>{placeholder}</span>
          </span>
        )}
        {busy ? <LoaderCircle size={18} className={styles.selectSpinner} aria-hidden="true" /> : <ChevronDown size={18} className={styles.chevron} aria-hidden="true" />}
      </button>
      {open && trigger ? (
        <SelectMenu
          anchor={trigger}
          id={listId}
          label={placeholder}
          items={items}
          selected={current}
          multi={multi}
          full={multi && current.length >= max}
          search={kind !== null}
          query={query}
          onQuery={setQuery}
          onPick={choose}
          onClose={close}
        />
      ) : null}
    </>
  )
}

interface MenuProps {
  anchor: HTMLElement
  id: string
  label: string
  items: Item[]
  selected: Item[]
  multi: boolean
  /** Выбрано сколько можно: остальные варианты не отмечаются, пока не снимут какой-нибудь. */
  full: boolean
  search: boolean
  query: string
  onQuery: (q: string) => void
  onPick: (item: Item) => void
  onClose: (returnFocus: boolean) => void
}

/**
 * Выпадающий список поверх ленты: в самой ленте его обрезала бы прокрутка. Встаёт под кнопкой,
 * а если снизу мало места (список у поля ввода) — над ней. Закрывается кликом мимо, Esc,
 * прокруткой ленты и сменой размера окна — как меню сообщения.
 */
function SelectMenu({ anchor, id, label, items, selected, multi, full, search, query, onQuery, onPick, onClose }: MenuProps) {
  const ref = useRef<HTMLDivElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [active, setActive] = useState(0)

  // размер и место знаем только после отрисовки: ставим прямо в стиль до показа кадра
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const r = anchor.getBoundingClientRect()
    const below = window.innerHeight - r.bottom - 12
    const above = r.top - 12
    const flip = below < 240 && above > below
    const width = Math.min(Math.max(r.width, 240), window.innerWidth - 16)
    el.style.width = `${width}px`
    el.style.left = `${Math.max(8, Math.min(r.left, window.innerWidth - width - 8))}px`
    el.style.maxHeight = `${Math.max(160, Math.min(340, flip ? above : below))}px`
    if (flip) el.style.bottom = `${window.innerHeight - r.top + 4}px`
    else el.style.top = `${r.bottom + 4}px`
    el.style.setProperty('--popover-origin', flip ? 'bottom center' : 'top center')
  }, [anchor])

  useEffect(() => {
    if (search) inputRef.current?.focus()
    else listRef.current?.focus()
  }, [search])

  // onClose новый на каждой отрисовке (в нём текущий выбор) — подписки обновляются вместе с ним
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (!ref.current?.contains(t) && !anchor.contains(t)) onClose(false)
    }
    const onScroll = (e: Event) => {
      if (!ref.current?.contains(e.target as Node)) onClose(false)
    }
    const onResize = () => onClose(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onResize)
    }
  }, [anchor, onClose])

  const current = Math.min(active, Math.max(items.length - 1, 0))
  useEffect(() => {
    revealIn(listRef.current, document.getElementById(`${id}-${current}`))
  }, [id, current])

  const isOn = (item: Item) => selected.some((s) => s.value === item.value)
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        if (items.length) setActive((current + 1) % items.length)
        break
      case 'ArrowUp':
        e.preventDefault()
        if (items.length) setActive((current - 1 + items.length) % items.length)
        break
      case 'Home':
        if (!search) {
          e.preventDefault()
          setActive(0)
        }
        break
      case 'End':
        if (!search) {
          e.preventDefault()
          setActive(Math.max(items.length - 1, 0))
        }
        break
      case 'Enter': {
        e.preventDefault()
        const item = items[current]
        if (item && (isOn(item) || !full)) onPick(item)
        break
      }
      case ' ':
        // пробел в поиске — это пробел, в самом списке — отметить
        if (e.target !== inputRef.current) {
          e.preventDefault()
          const item = items[current]
          if (item && (isOn(item) || !full)) onPick(item)
        }
        break
      case 'Escape':
        e.preventDefault()
        e.stopPropagation()
        onClose(true)
        break
      case 'Tab':
        onClose(false)
        break
      default:
        break
    }
  }

  const menu = (
    <div ref={ref} className={cx(styles.menu, 'klichat-popover')} onKeyDown={onKeyDown}>
      {search ? (
        <input
          ref={inputRef}
          className={styles.search}
          value={query}
          placeholder="Поиск"
          aria-label="Поиск"
          aria-controls={id}
          aria-activedescendant={items.length ? `${id}-${current}` : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={(e) => {
            onQuery(e.target.value)
            // новый запрос — выделение снова на первой строке
            setActive(0)
          }}
        />
      ) : null}
      <div
        ref={listRef}
        id={id}
        className={cx(styles.list, 'scroller')}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multi || undefined}
        aria-activedescendant={!search && items.length ? `${id}-${current}` : undefined}
        tabIndex={search ? -1 : 0}
      >
        {items.length === 0 ? <div className={styles.empty}>{search && query ? 'Ничего не нашлось' : 'Выбирать не из чего'}</div> : null}
        {items.map((item, i) => {
          const on = isOn(item)
          const blocked = full && !on
          return (
            <div
              key={item.key}
              id={`${id}-${i}`}
              role="option"
              aria-selected={on}
              aria-disabled={blocked || undefined}
              className={cx(styles.option, i === current && styles.optionActive, blocked && styles.optionBlocked)}
              onMouseEnter={() => setActive(i)}
              onMouseDown={(e) => {
                // фокус остаётся в поиске или в списке, а выбор срабатывает сразу
                e.preventDefault()
                if (!blocked) onPick(item)
              }}
            >
              {item.candidate ? <CandidateIcon c={item.candidate} size={24} /> : item.emoji ? <EmojiView emoji={item.emoji} /> : null}
              <span className={styles.optionText}>
                <span className={styles.optionLabel} style={item.candidate?.kind === 'role' && item.candidate.color ? { color: item.candidate.color } : undefined}>
                  {item.label}
                </span>
                {item.description ? <span className={styles.optionDescription}>{item.description}</span> : null}
              </span>
              {item.candidate?.bot ? <span className={styles.botTag}>БОТ</span> : null}
              {multi ? (
                <span className={cx(styles.check, on && styles.checkOn)} aria-hidden="true">
                  {on ? <Check size={14} strokeWidth={3} /> : null}
                </span>
              ) : on ? (
                <Check size={18} className={styles.checkMark} aria-hidden="true" />
              ) : null}
            </div>
          )
        })}
      </div>
    </div>
  )
  return createPortal(menu, document.body)
}
